<template>
  <div class="assistente-page">
    <Header />

    <main class="container">
      <!-- Primeira tela: ícone + texto + selos + CTA -->
      <!-- Referência: min-h-[80vh], ícone em círculo bg-primary/10 (Lucide shield), título display grande, lead muted, selos gap-8, CTA rounded-2xl -->
      <section v-if="etapa === 'intro'" class="intro-hero">
        <div class="intro-icon-circle" aria-hidden="true">
          <img src="../assets/Logo-Memorial-Parque-M.svg" alt="">
        </div>

        <h1 class="intro-title-display">Cuidado que abraça quem você ama.</h1>
        <p class="intro-lead-muted">
          Vamos encontrar juntos a proteção ideal para a sua família, de forma simples e humana.
        </p>

        <div class="intro-meta-row">
          <div class="intro-meta-item">
            <svg class="intro-meta-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <span>Até 13 vidas</span>
          </div>
          <div class="intro-meta-item">
            <svg class="intro-meta-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
            </svg>
            <span>Cobertura completa</span>
          </div>
        </div>

        <button type="button" class="intro-cta-ref" @click="irParaSelecaoTipo">
          Encontrar a solução ideal para mim
        </button>
      </section>

      <!-- Seleção de tipo de produto (layout referência) -->
      <section v-else-if="etapa === 'home'" class="home-screen">
        <div class="home-shell">
          <button type="button" class="btn-link home-back" @click="voltarParaIntro">
            <span class="chevron">‹</span>
            Voltar
          </button>

          <div class="home-content">
            <p class="home-kicker">Bem-vindo</p>
            <h2 class="home-title">O que você está procurando?</h2>
            <p class="home-lead">
              Selecione o tipo de produto para encontrarmos a melhor opção para você.
            </p>

            <div v-if="carregandoCategorias" class="loading home-loading">
              <div class="spinner"></div>
              <p>Carregando categorias...</p>
            </div>

            <div v-else-if="erroCategorias" class="error-box home-error">
              <p>{{ erroCategorias }}</p>
              <button class="btn-primary" @click="carregarCategorias">Tentar novamente</button>
            </div>

            <div v-else class="home-cards-grid">
              <button
                v-for="card in cardsCategorias"
                :key="card.key"
                type="button"
                class="home-card"
                :class="{ 'home-card--disabled': card.disabled }"
                :disabled="card.disabled"
                @click="card.onClick"
              >
                <div class="home-card-icon-wrap">
                  <!-- Lucide: shield-check -->
                  <svg
                    v-if="card.icon === 'shield-check'"
                    class="home-card-icon-svg"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                  <!-- Lucide: flame -->
                  <svg
                    v-else-if="card.icon === 'flame'"
                    class="home-card-icon-svg"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
                  </svg>
                  <!-- Lucide: landmark -->
                  <svg
                    v-else-if="card.icon === 'landmark'"
                    class="home-card-icon-svg"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <line x1="3" x2="21" y1="22" y2="22" />
                    <line x1="6" x2="6" y1="18" y2="11" />
                    <line x1="10" x2="10" y1="18" y2="11" />
                    <line x1="14" x2="14" y1="18" y2="11" />
                    <line x1="18" x2="18" y1="18" y2="11" />
                    <polygon points="12 2 20 7 4 7" />
                  </svg>
                  <!-- Lucide: flower2 -->
                  <svg
                    v-else-if="card.icon === 'flower2'"
                    class="home-card-icon-svg"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 5a3 3 0 1 1 3 3m-3-3a3 3 0 1 0-3 3m3-3v1M9 8a3 3 0 1 0 3 3M9 8h1m5 0a3 3 0 1 1-3 3m3-3h-1m-2 3v-1" />
                    <circle cx="12" cy="8" r="2" />
                    <path d="M12 10v12" />
                    <path d="M12 22c4.2 0 7-1.667 7-5-4.2 0-7 1.667-7 5Z" />
                    <path d="M12 22c-4.2 0-7-1.667-7-5 4.2 0 7 1.667 7 5Z" />
                  </svg>
                </div>
                <div class="home-card-text">
                  <h3 class="home-card-title">{{ card.title }}</h3>
                  <p class="home-card-desc">{{ card.description }}</p>
                </div>
                <span v-if="card.badge" class="home-card-badge">{{ card.badge }}</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Wizard de plano funerário -->
      <section v-else-if="etapa === 'wizard'" class="wizard">
        <div class="wizard-top">
          <button type="button" class="btn-link" @click="voltarWizard">
            <span class="chevron">‹</span>
            Voltar
          </button>
        </div>

        <!-- Passo 1 -->
        <div v-if="passoNumero === 1" class="step step-wizard-ref">
          <p class="wizard-step-kicker">Passo {{ passoNumero }} de 4</p>
          <h2 class="wizard-step-title">
            Antes de tudo, você — o titular do contrato — tem até 65 anos?
          </h2>
          <p class="wizard-step-lead">
            O titular do plano precisa ter no máximo 65 anos de idade.
          </p>

          <div class="wizard-step-actions">
            <button type="button" class="wizard-btn-primary" @click="definirTitularAte65(true)">
              Sim, tenho até 65
            </button>
            <button type="button" class="wizard-btn-secondary" @click="definirTitularAte65(false)">
              Não
            </button>
          </div>

          <div v-if="titularAte65 === false" class="notice wizard-step-notice">
            No momento, só conseguimos seguir com a contratação se o titular tiver até 65 anos.
          </div>
        </div>

        <!-- Passo 2 -->
        <div v-else-if="passoNumero === 2" class="step step-wizard-ref">
          <p class="wizard-step-kicker">Passo {{ passoNumero }} de 4</p>
          <h2 class="wizard-step-title">
            Para começar, conte-nos: quantas pessoas farão parte do plano?
          </h2>
          <p class="wizard-step-lead">
            Inclua você e todos os familiares que deseja proteger.
          </p>

          <div class="wizard-numbers-grid">
            <button
              v-for="n in 13"
              :key="n"
              type="button"
              class="wizard-num-btn"
              :class="{ 'wizard-num-btn--selected': totalVidas === n }"
              @click="selecionarTotalVidas(n)"
            >
              {{ n }}
            </button>
          </div>
          <p class="wizard-numbers-hint">Selecione de 1 a 13 pessoas</p>

          <div v-if="totalVidas != null" class="wizard-step-actions wizard-step-actions--mt">
            <button
              type="button"
              class="wizard-btn-primary"
              @click="avancarPasso2Para3"
            >
              Continuar
            </button>
          </div>

          <div v-if="planosParaPasso2.length" class="passo2-planos-wrap">
            <div class="passo2-planos-box">
              <p class="passo2-planos-title">
                {{ totalVidas != null ? 'Planos que atendem à sua escolha' : 'Planos disponíveis' }}
              </p>
              <div class="passo2-planos-carrossel">
              <div
                v-for="p in planosParaPasso2"
                :key="p.id"
                class="passo2-plano-card"
                :class="{ 'passo2-plano-card--bloqueado': p.bloqueado }"
              >
                <div v-if="p.bloqueado" class="passo2-plano-card__bloqueio" aria-hidden="true"></div>
                <span class="passo2-plano-card__nome">{{ p.nome || p.descricao }}</span>
                <span class="passo2-plano-card__cap">Até {{ p.capMax }} {{ p.capMax === 1 ? 'pessoa' : 'pessoas' }}</span>
                <span class="passo2-plano-card__valor">R$ {{ formatMoney(p.valor) }}/mês</span>
              </div>
            </div>
            </div>
          </div>
        </div>

        <!-- Passo 3 (sub-etapas: números → idade &gt;80 → bloqueio) -->
        <div v-else-if="passoNumero === 3" class="step step-wizard-ref">
          <!-- 3a — quantidade de agregados 66–80 -->
          <template v-if="passo3Sub === 'numeros'">
            <p class="wizard-step-kicker">Passo {{ passoNumero }} de 4</p>
            <h2 class="wizard-step-title">
              Dentre os beneficiários, quantos têm entre 66 e 80 anos?
            </h2>
            <p class="wizard-step-lead">
              Beneficiários entre 66 e 80 anos são incluídos como agregados, com um acréscimo de R$ 14,90 por pessoa.
            </p>

            <div class="wizard-numbers-flex">
              <button
                v-for="n in opcoesAgregados"
                :key="n"
                type="button"
                class="wizard-num-btn wizard-num-btn--compact"
                :class="{ 'wizard-num-btn--selected': qtdAgregados === n }"
                @click="selecionarAgregados(n)"
              >
                {{ n }}
              </button>
            </div>
            <p class="wizard-numbers-hint">
              Selecione 0 se nenhum beneficiário tem mais de 65 anos
            </p>

            <div v-if="qtdAgregados != null" class="wizard-step-actions wizard-step-actions--mt">
              <button
                type="button"
                class="wizard-btn-primary"
                @click="avancarPasso3Numeros"
              >
                Continuar
              </button>
            </div>

            <div v-if="planosParaPasso3.length" class="passo2-planos-wrap">
              <div class="passo2-planos-box">
                <p class="passo2-planos-title">
                  {{ qtdAgregados != null ? 'Planos que atendem à sua escolha' : 'Planos disponíveis' }}
                </p>
                <div class="passo2-planos-carrossel">
                  <div
                    v-for="p in planosParaPasso3"
                    :key="p.id"
                    class="passo2-plano-card"
                    :class="{ 'passo2-plano-card--bloqueado': p.bloqueado }"
                  >
                    <div v-if="p.bloqueado" class="passo2-plano-card__bloqueio" aria-hidden="true"></div>
                    <span class="passo2-plano-card__nome">{{ p.nome || p.descricao }}</span>
                    <span class="passo2-plano-card__cap">Até {{ p.capMax }} {{ p.capMax === 1 ? 'pessoa' : 'pessoas' }}{{ (p.qtdAgregados || 0) > 0 ? ` · ${p.qtdAgregados} agregado${p.qtdAgregados !== 1 ? 's' : ''}` : '' }}</span>
                    <span class="passo2-plano-card__valor">R$ {{ formatMoney(p.valor) }}/mês</span>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- 3b — algum beneficiário com mais de 80 anos (só se qtd &gt; 0) -->
          <template v-else-if="passo3Sub === 'idade80'">
            <p class="wizard-step-kicker">Passo {{ passoNumero }} de 4</p>
            <h2 class="wizard-step-title">
              Algum desses beneficiários tem mais de 80 anos?
            </h2>
            <p class="wizard-step-lead">
              A idade máxima para participar do plano é 80 anos.
            </p>
            <div class="wizard-step-actions wizard-step-actions--idade80">
              <button
                type="button"
                class="wizard-btn-secondary wizard-btn-px-lg"
                @click="responderBeneficiariosAcima80(true)"
              >
                Sim
              </button>
              <button
                type="button"
                class="wizard-btn-primary wizard-btn-px-lg"
                @click="responderBeneficiariosAcima80(false)"
              >
                Não, todos têm até 80
              </button>
            </div>
          </template>

          <!-- 3c — bloqueio: beneficiário acima de 80 -->
          <div v-else-if="passo3Sub === 'bloqueadoIdade'" class="wizard-block-screen">
            <div class="wizard-block-icon-wrap" aria-hidden="true">
              <svg
                class="wizard-block-icon"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
              </svg>
            </div>
            <h2 class="wizard-step-title">
              A idade limite para beneficiários é 80 anos
            </h2>
            <p class="wizard-step-lead wizard-block-lead">
              Infelizmente, não é possível incluir beneficiários com mais de 80 anos no plano. Caso tenha dúvidas, entre em contato conosco.
            </p>
            <button type="button" class="wizard-link-back" @click="voltarDeAlertaIdade80">
              ← Voltar
            </button>
          </div>
        </div>

        <!-- Passo 4 — layout referência: grid 1/2/3 col, cards rounded-3xl, ícone + badge último -->
        <div v-else class="step step-wizard-ref wizard-step-plans">
          <p class="wizard-step-kicker">Passo {{ passoNumero }} de 4</p>
          <h2 class="wizard-step-title wizard-step-title--plans">
            Ótimo! Com {{ totalVidas }} {{ totalVidas === 1 ? 'pessoa' : 'pessoas' }} protegidas, qual plano você prefere?
          </h2>
          <p class="wizard-step-lead wizard-step-lead--plans">
            Escolha o nível de cobertura que melhor se encaixa nas necessidades da sua família.
          </p>

          <div v-if="carregandoPlanos" class="loading wizard-plans-loading">
            <div class="spinner"></div>
            <p>Carregando planos...</p>
          </div>

          <div v-else-if="erroPlanos" class="error-box wizard-plans-error">
            <p>{{ erroPlanos }}</p>
            <button class="btn-primary" @click="carregarPlanos">Tentar novamente</button>
          </div>

          <div v-else-if="planosParaPasso4.length === 0" class="wizard-plans-empty">
            <p class="wizard-step-lead">
              Nenhum plano atende ao perfil escolhido ({{ totalVidas }} pessoa{{ totalVidas !== 1 ? 's' : '' }}{{ qtdAgregados > 0 ? `, ${qtdAgregados} com 66–80 anos` : '' }}).
            </p>
          </div>

          <div
            v-else
            class="plans-grid-ref"
            :class="{ 'plans-grid-ref--few': planosParaPasso4.length <= 2 }"
          >
            <button
              v-for="(plano, idx) in planosParaPasso4"
              :key="plano.id"
              type="button"
              class="plan-card-ref"
              :class="{
                'plan-card-ref--highlighted': idx === planosParaPasso4.length - 1,
                'plan-card-ref--recommended': idx === 0
              }"
              @click="abrirResumoPlano(plano)"
            >
              <div class="plan-card-ref__badges">
                <div
                  v-if="idx === 0"
                  class="plan-badge-ref plan-badge-ref--recomendado"
                >
                  Recomendado
                </div>
                <div
                  v-if="idx === planosParaPasso4.length - 1"
                  class="plan-badge-ref plan-badge-ref--completo"
                >
                  Mais completo
                </div>
              </div>

              <div
                class="plan-card-ref__icon-wrap"
                :class="{
                  'plan-card-ref__icon-wrap--highlight': idx === planosParaPasso4.length - 1,
                  'plan-card-ref__icon-wrap--recommended': idx === 0
                }"
              >
                <!-- heart -->
                <svg
                  v-if="planoIconKey(idx) === 'heart'"
                  class="plan-card-ref__icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                </svg>
                <!-- shield-check -->
                <svg
                  v-else-if="planoIconKey(idx) === 'shield-check'"
                  class="plan-card-ref__icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
                <!-- gem -->
                <svg
                  v-else-if="planoIconKey(idx) === 'gem'"
                  class="plan-card-ref__icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M6 3h12l4 6-10 13L2 9Z" />
                  <path d="M11 3 8 9l4 13 4-13-3-6" />
                  <path d="M2 9h20" />
                </svg>
                <!-- hand-heart -->
                <svg
                  v-else-if="planoIconKey(idx) === 'hand-heart'"
                  class="plan-card-ref__icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M11 14h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 16" />
                  <path d="m7 20 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9" />
                  <path d="m2 15 6 6" />
                  <path d="M19.5 8.5c.7-.7 1.5-1.6 1.5-2.7A2.73 2.73 0 0 0 16 4a2.78 2.78 0 0 0-5 1.8c0 1.2.8 2 1.5 2.8L16 12Z" />
                </svg>
                <!-- sparkles (último plano) -->
                <svg
                  v-else
                  class="plan-card-ref__icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                  <path d="M20 3v4" />
                  <path d="M22 5h-4" />
                  <path d="M4 17v2" />
                  <path d="M5 18H3" />
                </svg>
              </div>

              <h3 class="plan-card-ref__title">
                {{ plano.nome || plano.descricao }}
              </h3>
              <p
                v-if="plano.nome && plano.descricao"
                class="plan-card-ref__desc"
              >
                {{ plano.descricao }}
              </p>

              <div class="plan-card-ref__price">
                <span class="plan-card-ref__price-prefix">a partir de</span>
                <span class="plan-card-ref__price-value">R$ {{ formatMoney(plano.valor) }}</span>
                <span class="plan-card-ref__price-suffix">/mês</span>
              </div>
            </button>
          </div>
        </div>
      </section>

      <section v-else-if="etapa === 'resumoPlano'" class="plano-resumo-screen">
        <div class="wizard-top">
          <button type="button" class="btn-link" @click="voltarParaListaPlanos">
            <span class="chevron">‹</span>
            Voltar para os planos
          </button>
        </div>
        <div class="plano-resumo-header" v-if="planoSelecionado">
          <p class="plano-resumo-kicker">Sua recomendação</p>
          <h2 class="plano-resumo-title">
            Com base nas {{ totalVidas }} {{ totalVidas === 1 ? 'pessoa' : 'pessoas' }} que você mencionou, este é o plano ideal:
          </h2>
        </div>
        <div v-if="planoSelecionado" class="plano-resumo-card">
          <span class="plano-resumo-badge">Melhor escolha para sua família</span>
          <h3 class="plano-resumo-nome">{{ planoSelecionado.nome || planoSelecionado.descricao }}</h3>
          <div class="plano-resumo-preco">
            <span class="valor">R$ {{ formatMoney(planoSelecionado.valor) }}</span>
            <span class="sufixo">/mês</span>
          </div>

          <div class="plano-resumo-itens-wrap">
          <div v-if="carregandoItensPlano" class="loading plano-resumo-loading">
            <div class="spinner"></div>
            <p>Carregando coberturas...</p>
          </div>

          <template v-else>
            <ul v-if="itensPlanoDestaque.length" class="plano-resumo-lista">
              <li v-for="item in itensPlanoDestaque" :key="'lista-destaque-' + item.id">
                {{ item.descricao }}
              </li>
            </ul>
            <p v-else class="plano-resumo-sem-destaque">
              Este plano não possui itens de destaque cadastrados.
            </p>

            <div v-if="itensPlanoComplementares.length" class="plano-resumo-cobertura">
              <p class="plano-resumo-cobertura-titulo">
                <svg class="plano-resumo-cobertura-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
                </svg>
                Ver cobertura completa
              </p>
              <ul class="plano-resumo-lista plano-resumo-lista--completa plano-resumo-lista--colunas">
                <li v-for="item in itensPlanoComplementares" :key="'lista-completa-' + item.id">
                  {{ item.descricao }}
                </li>
              </ul>
            </div>
          </template>
          </div>

          <button type="button" class="wizard-btn-primary plano-resumo-cta" @click="confirmarPlanoEIrCheckout">
            Garantir proteção
          </button>
        </div>
      </section>
    </main>

    <Footer />
  </div>
</template>

<script>
import Header from '../components/Header.vue'
import Footer from '../components/Footer.vue'
import categoriaService from '../services/categoriaService'
import produtoService from '../services/produtoService'
import itemPlanoService from '../services/itemPlanoService'
import carrinhoStore from '../store/carrinho'

export default {
  name: 'Assistente',
  components: { Header, Footer },
  data() {
    return {
      etapa: 'intro', // 'intro' | 'home' | 'wizard' | 'resumoPlano'
      passoNumero: 1,

      categorias: [],
      carregandoCategorias: false,
      erroCategorias: null,

      planos: [],
      carregandoPlanos: false,
      erroPlanos: null,
      itensPlanoCatalogo: [],
      carregandoItensPlano: false,

      titularAte65: null,
      totalVidas: null,
      qtdAgregados: null,
      /** Passo 3: 'numeros' | 'idade80' | 'bloqueadoIdade' */
      passo3Sub: 'numeros',
      /** Após pergunta dos 80 anos: false = pode seguir; null = não aplicável */
      beneficiarioAlgumAcima80: null,
      planoSelecionado: null
    }
  },
  computed: {
    categoriaPlanos() {
      // Preferência: categoria "Planos Funerários" se existir
      return this.categorias.find(c => (c.descricao || '').toLowerCase().includes('plano')) || null
    },
    cardsCategorias() {
      const planosTitle = 'Plano Assistencial Funerário'
      const planosDescription =
        'Proteção completa para você e sua família com cobertura funeral.'

      const cremacaoTitle = 'Cremação'
      const cremacaoDescription =
        'Serviço de cremação com toda a assistência necessária.'

      const jazigoTitle = 'Jazigo'
      const jazigoDescription =
        'Aquisição e manutenção de jazigos em cemitérios parceiros.'

      const floriculturaTitle = 'Floricultura'
      const floriculturaDescription =
        'Arranjos florais e homenagens para seus entes queridos.'

      return [
        {
          key: 'planos',
          icon: 'shield-check',
          title: planosTitle,
          description: planosDescription,
          badge: null,
          disabled: false,
          onClick: this.iniciarWizard
        },
        {
          key: 'cremacao',
          icon: 'flame',
          title: cremacaoTitle,
          description: cremacaoDescription,
          badge: 'Em breve',
          disabled: true,
          onClick: () => {}
        },
        {
          key: 'jazigo',
          icon: 'landmark',
          title: jazigoTitle,
          description: jazigoDescription,
          badge: 'Em breve',
          disabled: true,
          onClick: () => {}
        },
        {
          key: 'floricultura',
          icon: 'flower2',
          title: floriculturaTitle,
          description: floriculturaDescription,
          badge: 'Em breve',
          disabled: true,
          onClick: () => {}
        }
      ]
    },
    opcoesAgregados() {
      return Array.from({ length: 8 }, (_, i) => i)
    },
    /** Planos para preview no passo 2: lista com capMax e bloqueado por totalVidas */
    planosParaPasso2() {
      const lista = Array.isArray(this.planos) ? [...this.planos] : []
      const totalVidas = Number(this.totalVidas) || 0
      const base = lista
        .filter(p => {
          if (p.ativo === false) return false
          if (Number(p.valor) <= 0) return false
          return p.planoFunerario === true
        })
        .sort((a, b) => (Number(a.valor) || 0) - (Number(b.valor) || 0))
      return base.map(p => {
        const capMax = 1 + (Number(p.qtdDependentes) || 0)
        const bloqueado = totalVidas > 0 && capMax < totalVidas
        return { ...p, capMax, bloqueado }
      })
    },
    /** Planos para preview no passo 3: filtro totalVidas + qtdAgregados */
    planosParaPasso3() {
      const totalVidas = Number(this.totalVidas) || 0
      const qtdAgregados = Number(this.qtdAgregados) || 0
      return this.planosParaPasso2.map(p => {
        const qtdAggProd = Number(p.qtdAgregados) || 0
        const bloqueadoPorCap = totalVidas > 0 && p.capMax < totalVidas
        const bloqueadoPorAgg = qtdAggProd < qtdAgregados
        const bloqueado = bloqueadoPorCap || bloqueadoPorAgg
        return { ...p, bloqueado }
      })
    },
    /** Planos para passo 4: somente os que passaram nos filtros do passo 2 e 3 */
    planosParaPasso4() {
      return this.planosParaPasso3.filter(p => !p.bloqueado)
    },
    idsItensPlanoSelecionado() {
      return this.normalizarItensPlanoIds(this.planoSelecionado)
    },
    itensPlanoDoPlanoSelecionado() {
      if (!this.idsItensPlanoSelecionado.length) return []
      const ids = new Set(this.idsItensPlanoSelecionado)
      return this.itensPlanoCatalogo.filter(item => ids.has(Number(item.id)))
    },
    itensPlanoDestaque() {
      return this.itensPlanoDoPlanoSelecionado.filter(item => Boolean(item.destaque))
    },
    itensPlanoComplementares() {
      return this.itensPlanoDoPlanoSelecionado.filter(item => !item.destaque)
    }
  },
  mounted() {
    this.carregarCategorias()
  },
  methods: {
    formatMoney(value) {
      return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(value || 0)
    },
    /** Ícone por posição (último = sparkles / “Mais completo”) — alinhado ao layout de referência */
    planoIconKey(idx) {
      const total = this.planosParaPasso4.length
      if (total === 0) return 'heart'
      if (idx === total - 1) return 'sparkles'
      const keys = ['heart', 'shield-check', 'gem', 'hand-heart', 'heart']
      return keys[idx % keys.length]
    },
    async carregarCategorias() {
      this.carregandoCategorias = true
      this.erroCategorias = null
      try {
        this.categorias = await categoriaService.listarCategorias()
      } catch (e) {
        console.error('Erro ao carregar categorias:', e)
        this.erroCategorias = 'Não foi possível carregar as categorias. Tente novamente.'
      } finally {
        this.carregandoCategorias = false
      }
    },
    irParaSelecaoTipo() {
      this.etapa = 'home'
      if (!this.categorias.length && !this.carregandoCategorias) {
        this.carregarCategorias()
      }
    },
    voltarParaIntro() {
      this.etapa = 'intro'
    },
    iniciarWizard() {
      this.etapa = 'wizard'
      this.passoNumero = 1
      this.titularAte65 = null
      this.totalVidas = null
      this.qtdAgregados = null
      this.passo3Sub = 'numeros'
      this.beneficiarioAlgumAcima80 = null
      this.planos = []
      this.erroPlanos = null
      this.planoSelecionado = null
    },
    voltarWizard() {
      if (this.passoNumero === 4) {
        this.passoNumero = 3
        this.passo3Sub =
          this.qtdAgregados > 0 ? 'idade80' : 'numeros'
        return
      }
      if (this.passoNumero === 3) {
        if (this.passo3Sub === 'bloqueadoIdade') {
          this.passo3Sub = 'idade80'
          return
        }
        if (this.passo3Sub === 'idade80') {
          this.passo3Sub = 'numeros'
          return
        }
        this.passoNumero = 2
        return
      }
      if (this.passoNumero > 1) {
        this.passoNumero -= 1
        return
      }
      this.etapa = 'home'
    },
    definirTitularAte65(valor) {
      this.titularAte65 = valor
      if (valor) {
        this.passoNumero = 2
        if (!this.planos.length) this.carregarPlanos()
      }
    },
    selecionarTotalVidas(n) {
      this.totalVidas = n
      this.qtdAgregados = null
      this.passo3Sub = 'numeros'
      this.beneficiarioAlgumAcima80 = null
    },
    avancarPasso2Para3() {
      if (this.totalVidas == null) return
      this.passoNumero = 3
    },
    irPasso4() {
      this.passo3Sub = 'numeros'
      this.passoNumero = 4
      this.carregarPlanos()
    },
    selecionarAgregados(n) {
      this.qtdAgregados = n
    },
    avancarPasso3Numeros() {
      if (this.qtdAgregados == null) return
      if (this.qtdAgregados === 0) {
        this.beneficiarioAlgumAcima80 = null
        this.irPasso4()
      } else {
        this.passo3Sub = 'idade80'
      }
    },
    responderBeneficiariosAcima80(simAlgumAcima) {
      if (simAlgumAcima) {
        this.beneficiarioAlgumAcima80 = true
        this.passo3Sub = 'bloqueadoIdade'
        return
      }
      this.beneficiarioAlgumAcima80 = false
      this.irPasso4()
    },
    voltarDeAlertaIdade80() {
      this.passo3Sub = 'idade80'
    },
    async carregarPlanos() {
      this.carregandoPlanos = true
      this.erroPlanos = null
      try {
        const categoriaId = this.categoriaPlanos?.id
        const params = categoriaId ? { categoriaId } : {}
        const raw = await produtoService.listarProdutos(params)
        const produtos = Array.isArray(raw) ? raw : (raw?.content || raw?.data || [])

        // Filtrar para planos funerários (quando o backend disponibiliza a flag)
        const filtrados = (produtos || []).filter(p => {
          if (p.ativo === false) return false
          if (p.planoFunerario === true) return true
          if (categoriaId) return Number(p.categoriaId) === Number(categoriaId)
          return true
        })

        // Só mostrar itens com preço válido
        this.planos = filtrados.filter(p => Number(p.valor) > 0)
      } catch (e) {
        console.error('Erro ao carregar planos:', e)
        this.erroPlanos = 'Não foi possível carregar os planos. Tente novamente.'
      } finally {
        this.carregandoPlanos = false
      }
    },
    normalizarItensPlanoIds(produto) {
      if (!produto) return []
      if (Array.isArray(produto.itensPlanoIds) && produto.itensPlanoIds.length) {
        return produto.itensPlanoIds.map(Number).filter((id) => !Number.isNaN(id))
      }
      if (Array.isArray(produto.itensPlano) && produto.itensPlano.length) {
        return produto.itensPlano
          .map((item) => {
            const raw = item?.id != null ? item.id : item
            return Number(raw)
          })
          .filter((id) => !Number.isNaN(id))
      }
      return []
    },
    async carregarItensPlanoCatalogo() {
      if (this.carregandoItensPlano) return
      this.carregandoItensPlano = true
      try {
        const data = await itemPlanoService.listar()
        this.itensPlanoCatalogo = Array.isArray(data) ? data : (data?.content || data?.data || [])
      } catch (e) {
        console.error('Erro ao carregar itens de plano:', e)
        this.itensPlanoCatalogo = []
      } finally {
        this.carregandoItensPlano = false
      }
    },
    async abrirResumoPlano(plano) {
      this.planoSelecionado = plano
      this.etapa = 'resumoPlano'
      if (!this.itensPlanoCatalogo.length) {
        await this.carregarItensPlanoCatalogo()
      }
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    voltarParaListaPlanos() {
      this.etapa = 'wizard'
      this.passoNumero = 4
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    confirmarPlanoEIrCheckout() {
      const plano = this.planoSelecionado
      if (!plano) return
      try {
        // Guardar o contexto do assistente (útil para analytics/regras futuras)
        localStorage.setItem(
          'assistente_state',
          JSON.stringify({
            titularAte65: this.titularAte65,
            totalVidas: this.totalVidas,
            qtdAgregados: this.qtdAgregados,
            beneficiarioAlgumAcima80: this.beneficiarioAlgumAcima80,
            planoId: plano.id
          })
        )
      } catch (e) {
        console.warn('Não foi possível salvar assistente_state:', e)
      }

      carrinhoStore.adicionarItem(plano, 1)
      this.$router.push('/carrinho')
    }
  }
}
</script>

<style scoped>
.assistente-page {
  min-height: 100vh;
  background:
    radial-gradient(1200px 600px at 20% 0%, rgba(72, 187, 120, 0.18), rgba(255, 255, 255, 0) 60%),
    radial-gradient(1000px 520px at 85% 15%, rgba(16, 185, 129, 0.12), rgba(255, 255, 255, 0) 55%),
    linear-gradient(180deg, #ffffff 0%, #f7faf9 60%, #ffffff 100%);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 56px 20px 72px;
}

/* Primeira tela — alinhada ao layout de referência (Tailwind / Lucide) */
.intro-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
  text-align: center;
  padding: 24px 24px 48px;
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
}

.intro-icon-circle {
  margin-bottom: 1rem;
}

.intro-shield-lucide {
  width: 2.5rem;
  height: 2.5rem;
  color: #34894d;
  display: block;
}

.intro-title-display {
  margin: 0;
  max-width: 48rem;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(2.25rem, 5.5vw, 3.75rem);
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: #0f172a;
}

.intro-lead-muted {
  margin: 1.5rem 0 0;
  max-width: 36rem;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(1.125rem, 2.2vw, 1.25rem);
  line-height: 1.55;
  font-weight: 400;
  color: #64748b;
}

.intro-meta-row {
  margin-top: 2.5rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  color: #475569;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.intro-meta-item {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.intro-meta-icon {
  width: 1rem;
  height: 1rem;
  color: #34894d;
  flex-shrink: 0;
}

.intro-cta-ref {
  margin-top: 3rem;
  padding: 1rem 2.5rem;
  border: none;
  border-radius: 1rem;
  background: #1f6f36;
  color: #ffffff;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.3;
  cursor: pointer;
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.08);
  transition: box-shadow 0.3s ease, transform 0.2s ease, background 0.2s ease;
}

.intro-cta-ref:hover {
  background: #195f2f;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.12);
  transform: translateY(-1px);
}

.intro-cta-ref:active {
  transform: translateY(0);
}

.intro-back-home {
  align-self: flex-start;
  margin-bottom: 8px;
}

/* Segunda tela — seleção de tipo (referência Tailwind) */
.home-screen {
  width: 100%;
}

.home-shell {
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px 24px 48px;
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.home-back {
  align-self: flex-start;
  margin-bottom: 8px;
}

.home-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 6px;
}

.home-kicker {
  margin: 0 0 1rem;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: #34894d;
}

.home-title {
  margin: 0;
  max-width: 42rem;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(1.875rem, 4vw, 2.25rem);
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.02em;
  color: #0f172a;
}

.home-lead {
  margin: 0.75rem 0 0;
  max-width: 28rem;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 1rem;
  line-height: 1.55;
  color: #64748b;
}

.home-loading,
.home-error {
  margin-top: 2.5rem;
}

.home-cards-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  width: 100%;
  max-width: 42rem;
  margin-top: 2.5rem;
}

@media (min-width: 640px) {
  .home-cards-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.home-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 1.5rem;
  box-shadow: 0 10px 30px -10px rgba(15, 23, 42, 0.08);
  transition: border-color 0.3s ease, box-shadow 0.3s ease, transform 0.2s ease;
  font-family: inherit;
}

.home-card:hover:not(:disabled):not(.home-card--disabled) {
  border-color: rgba(52, 137, 77, 0.55);
  box-shadow: 0 14px 40px -12px rgba(15, 23, 42, 0.12);
}

.home-card--disabled,
.home-card:disabled {
  opacity: 0.85;
  cursor: not-allowed;
}

.home-card-icon-wrap {
  width: 3rem;
  height: 3rem;
  border-radius: 1rem;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.3s ease;
}

.home-card:hover:not(:disabled):not(.home-card--disabled) .home-card-icon-wrap {
  background: rgba(52, 137, 77, 0.1);
}

.home-card-icon-svg {
  width: 1.5rem;
  height: 1.5rem;
  color: #34894d;
}

.home-card-text {
  width: 100%;
}

.home-card-title {
  margin: 0;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.125rem;
  font-weight: 700;
  color: #0f172a;
  transition: color 0.3s ease;
}

.home-card:hover:not(:disabled):not(.home-card--disabled) .home-card-title {
  color: #34894d;
}

.home-card-desc {
  margin: 0.25rem 0 0;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #64748b;
}

.home-card-badge {
  align-self: center;
  margin-top: 0.25rem;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  background: #fef3c7;
  color: #78350f;
}

.hero {
  padding-top: 8px;
  max-width: 980px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.hero-kicker {
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #1f6f36;
  margin: 0 0 14px 0;
  text-align: center;
}

.hero-title {
  font-size: clamp(30px, 4.2vw, 52px);
  font-weight: 900;
  line-height: 1.1;
  color: #0f172a;
  margin: 0 auto;
  max-width: 860px;
  text-align: center;
  letter-spacing: -0.02em;
}

.hero-highlights {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 26px 0 44px;
}

.highlight {
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 999px;
  padding: 12px 16px;
  text-align: center;
  box-shadow:
    0 1px 0 rgba(15, 23, 42, 0.04),
    0 12px 32px rgba(15, 23, 42, 0.06);
  backdrop-filter: blur(10px);
}

.highlight-value {
  font-weight: 800;
  color: #0f172a;
  font-size: 13px;
}

.section-header {
  margin: 8px 0 18px;
  text-align: center;
}

.section-title {
  font-size: 22px;
  font-weight: 900;
  color: #0f172a;
  margin: 0 0 6px;
}

.section-subtitle {
  margin: 0;
  color: #6b7280;
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.65;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-top: 22px;
}

.tipo-card {
  background: rgba(255, 255, 255, 0.86);
  border: 1px solid rgba(15, 23, 42, 0.10);
  border-radius: 26px;
  padding: 22px 22px;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s, transform 0.2s, box-shadow 0.2s;
  box-shadow:
    0 1px 0 rgba(15, 23, 42, 0.05),
    0 22px 60px rgba(15, 23, 42, 0.08);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
}

.tipo-card:hover:not(.disabled) {
  border-color: rgba(31, 111, 54, 0.5);
  transform: translateY(-2px);
  box-shadow:
    0 1px 0 rgba(15, 23, 42, 0.05),
    0 30px 80px rgba(15, 23, 42, 0.12);
}

.tipo-card.disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.tipo-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.tipo-card-title {
  font-size: 16px;
  font-weight: 900;
  margin: 0;
  color: #0f172a;
}

.tipo-card-desc {
  margin: 0;
  color: #6b7280;
  line-height: 1.55;
}

.badge {
  font-size: 12px;
  font-weight: 800;
  background: #fff7ed;
  color: #7c2d12;
  border: 1px solid #fed7aa;
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
}

.wizard-top {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 16px;
  max-width: 980px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  padding: 0 24px;
  box-sizing: border-box;
}

.btn-link {
  background: transparent;
  border: none;
  padding: 0;
  color: #34894d;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-link:hover {
  text-decoration: underline;
}

.chevron {
  font-size: 18px;
  line-height: 1;
}

.step {
  min-height: 62vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 18px 24px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* Passo 1 — layout referência (kicker + título + lead + 2 botões horizontais) */
.step-wizard-ref {
  min-height: 70vh;
  padding-left: 24px;
  padding-right: 24px;
  box-sizing: border-box;
}

.wizard-step-kicker {
  margin: 0 0 1rem;
  width: 100%;
  text-align: center;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: #34894d;
}

.wizard-step-title {
  margin: 0;
  width: 100%;
  max-width: 42rem;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(1.875rem, 4vw, 2.25rem);
  font-weight: 500;
  line-height: 1.35;
  letter-spacing: -0.02em;
  color: #0f172a;
  text-align: center;
}

.wizard-step-lead {
  margin: 0.75rem 0 0;
  width: 100%;
  max-width: 28rem;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 1rem;
  line-height: 1.55;
  font-weight: 400;
  color: #64748b;
  text-align: center;
}

.wizard-step-actions {
  margin-top: 2.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  align-items: center;
}

.wizard-step-actions--idade80 {
  flex-wrap: nowrap;
  gap: 1rem;
}

@media (max-width: 640px) {
  .wizard-step-actions--idade80 {
    flex-direction: column;
    flex-wrap: wrap;
    width: 100%;
    max-width: 22rem;
  }

  .wizard-step-actions--idade80 .wizard-btn-primary,
  .wizard-step-actions--idade80 .wizard-btn-secondary {
    width: 100%;
  }
}

.wizard-btn-px-lg {
  padding-left: 2.5rem;
  padding-right: 2.5rem;
}

.wizard-block-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 36rem;
  text-align: center;
}

.wizard-block-icon-wrap {
  width: 4rem;
  height: 4rem;
  border-radius: 9999px;
  background: rgba(220, 38, 38, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.wizard-block-icon {
  width: 2rem;
  height: 2rem;
  color: #dc2626;
}

.wizard-block-lead {
  max-width: 28rem;
}

.wizard-link-back {
  margin-top: 2rem;
  padding: 0.5rem;
  border: none;
  background: none;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  color: #34894d;
  cursor: pointer;
  transition: color 0.2s ease;
}

.wizard-link-back:hover {
  color: #195f2f;
}

.wizard-btn-primary {
  padding: 1rem 2.5rem;
  border: none;
  border-radius: 1rem;
  background: #1f6f36;
  color: #ffffff;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 1.125rem;
  font-weight: 500;
  line-height: 1.3;
  cursor: pointer;
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.08);
  transition: box-shadow 0.3s ease, transform 0.2s ease, background 0.2s ease;
}

.wizard-btn-primary:hover {
  background: #195f2f;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12);
}

.wizard-btn-secondary {
  padding: 1rem 2.5rem;
  border-radius: 1rem;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: #ffffff;
  color: #0f172a;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 1.125rem;
  font-weight: 500;
  line-height: 1.3;
  cursor: pointer;
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.06);
  transition: border-color 0.3s ease, box-shadow 0.3s ease, transform 0.2s ease;
}

.wizard-btn-secondary:hover {
  border-color: rgba(52, 137, 77, 0.55);
  box-shadow: 0 8px 28px rgba(15, 23, 42, 0.1);
}

.wizard-step-notice {
  margin-top: 1.5rem;
  max-width: 28rem;
  text-align: center;
}

.wizard {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.step-title {
  font-size: clamp(22px, 3.4vw, 40px);
  font-weight: 900;
  line-height: 1.12;
  color: #0f172a;
  max-width: 860px;
  margin: 0 auto;
  text-align: center;
  letter-spacing: -0.02em;
}

.step-subtitle {
  margin: 10px auto 0;
  max-width: 660px;
  text-align: center;
  color: #6b7280;
  line-height: 1.6;
}

.options-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  max-width: 420px;
  margin: 28px auto 0;
}

.option-btn {
  background: #1f6f36;
  color: #ffffff;
  border: none;
  border-radius: 999px;
  padding: 14px 18px;
  font-weight: 900;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s, background 0.15s;
}

.option-btn:hover {
  background: #195f2f;
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(52, 137, 77, 0.25);
}

.option-btn-outline {
  background: #ffffff;
  color: #111827;
  border: 2px solid rgba(15, 23, 42, 0.12);
}

.option-btn-outline:hover {
  background: #f9fafb;
  border-color: #34894d;
  box-shadow: 0 8px 18px rgba(0,0,0,0.06);
}

/* Passo 2 — grid de números (referência: grid-cols-5 gap-3 max-w-md, cards quadrados) */
.wizard-numbers-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.75rem;
  width: 100%;
  max-width: 28rem;
  margin: 2.5rem auto 0;
  box-sizing: border-box;
}

.wizard-num-btn {
  aspect-ratio: 1;
  width: 100%;
  min-height: 0;
  padding: 0;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 1rem;
  background: #ffffff;
  box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1;
  color: #0f172a;
  cursor: pointer;
  transition: border-color 0.3s ease, background 0.3s ease, box-shadow 0.3s ease, transform 0.15s ease;
}

.wizard-num-btn:hover {
  border-color: rgba(52, 137, 77, 0.55);
  background: rgba(52, 137, 77, 0.05);
}

.wizard-num-btn--selected {
  border-color: #1f6f36;
  background: #1f6f36;
  color: #ffffff;
  box-shadow: 0 10px 30px -10px rgba(52, 137, 77, 0.35);
}

/* Passo 3 — flex wrap (referência: flex flex-wrap justify-center gap-3 max-w-md, cards w-14 h-14 rounded-2xl) */
.wizard-numbers-flex {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  max-width: 28rem;
  margin: 2.5rem auto 0;
  box-sizing: border-box;
}

.wizard-num-btn.wizard-num-btn--compact {
  width: 3.5rem;
  height: 3.5rem;
  min-width: 3.5rem;
  flex-shrink: 0;
}

.wizard-numbers-hint {
  margin: 1rem 0 0;
  width: 100%;
  max-width: 28rem;
  text-align: center;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
  line-height: 1.4;
}

.wizard-step-actions--mt {
  margin-top: 2rem;
}

/* Passo 2 — carrossel de planos (preview de capacidade) */
.passo2-planos-wrap {
  margin-top: 3rem;
  width: 100%;
  display: flex;
  justify-content: center;
}

.passo2-planos-box {
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  padding: 1.25rem 1.5rem;
  background: rgba(241, 245, 249, 0.9);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 1.25rem;
}

.passo2-planos-title {
  margin: 0 0 1rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #475569;
  text-align: center;
}

.passo2-planos-carrossel {
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  padding: 0.5rem 0 1rem;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  justify-content: center;
  flex-wrap: nowrap;
}

.passo2-planos-carrossel::-webkit-scrollbar {
  height: 6px;
}

.passo2-planos-carrossel::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 0.06);
  border-radius: 3px;
}

.passo2-planos-carrossel::-webkit-scrollbar-thumb {
  background: rgba(15, 23, 42, 0.2);
  border-radius: 3px;
}

.passo2-plano-card {
  flex: 0 0 180px;
  min-width: 180px;
  scroll-snap-align: start;
  position: relative;
  padding: 1rem;
  border-radius: 1rem;
  border: 1px solid rgba(15, 23, 42, 0.1);
  background: #ffffff;
  box-shadow: 0 4px 12px -4px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.passo2-plano-card--bloqueado {
  opacity: 0.7;
  background: #f8fafc;
}

.passo2-plano-card__bloqueio {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: rgba(248, 250, 252, 0.95);
  border-radius: 1rem;
  color: #b91c1c;
  font-size: 0.75rem;
  font-weight: 600;
}

.passo2-plano-card__nome {
  font-size: 0.9rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.3;
}

.passo2-plano-card__cap {
  font-size: 0.8rem;
  color: #64748b;
}

.passo2-plano-card__valor {
  font-size: 0.85rem;
  font-weight: 700;
  color: #1f6f36;
  margin-top: 0.25rem;
}

.passo2-plano-card--bloqueado .passo2-plano-card__nome,
.passo2-plano-card--bloqueado .passo2-plano-card__cap,
.passo2-plano-card--bloqueado .passo2-plano-card__valor {
  opacity: 0.5;
}

/* Passo 4 — referência: min-h 70vh, py-16, grid 1 sm:2 lg:3, cards rounded-3xl */
.wizard-step-plans {
  padding-top: 4rem;
  padding-bottom: 4rem;
}

.wizard-step-title--plans {
  max-width: 42rem;
}

.wizard-step-lead--plans {
  margin-top: 0.75rem;
}

/* Resumo do plano (tela intermediária antes do checkout) */
.plano-resumo-screen {
  width: 100%;
}

.plano-resumo-header {
  width: 100%;
  max-width: 760px;
  margin: 0 auto 18px;
  text-align: center;
}

.plano-resumo-card {
  width: 100%;
  max-width: 680px;
  margin: 0 auto;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 24px;
  padding: 34px 28px;
  box-shadow: 0 14px 40px -12px rgba(15, 23, 42, 0.1);
}

.plano-resumo-kicker {
  margin: 0;
  font-size: 0.85rem;
  font-weight: 700;
  color: #34894d;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.plano-resumo-title {
  margin: 10px 0 18px;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(1.35rem, 2.8vw, 2rem);
  font-weight: 600;
  line-height: 1.35;
  color: #0f172a;
  text-align: center;
}

.plano-resumo-badge {
  display: inline-block;
  padding: 5px 12px;
  border-radius: 999px;
  background: #d1fae5;
  color: #065f46;
  font-size: 12px;
  font-weight: 700;
}

.plano-resumo-nome {
  margin: 12px 0 8px;
  font-size: 1.45rem;
  font-weight: 700;
  color: #111827;
}

.plano-resumo-preco {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.plano-resumo-preco .valor {
  font-size: 2rem;
  font-weight: 800;
  color: #1f6f36;
}

.plano-resumo-preco .sufixo {
  font-size: 1rem;
  color: #475569;
}

.plano-resumo-itens-wrap {
  margin-top: 24px;
}

.plano-resumo-lista {
  margin: 0;
  padding-left: 20px;
  color: #334155;
  line-height: 1.65;
}

.plano-resumo-lista li {
  margin-bottom: 6px;
}

.plano-resumo-sem-destaque {
  margin: 0;
  color: #64748b;
  font-size: 0.95rem;
}

.plano-resumo-cobertura {
  margin-top: 28px;
}

.plano-resumo-cobertura-titulo {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px;
  font-size: 1rem;
  font-weight: 700;
  color: #1f6f36;
}

.plano-resumo-cobertura-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.plano-resumo-lista--completa {
  margin-top: 10px;
}

.plano-resumo-lista--colunas {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 24px;
}

@media (max-width: 480px) {
  .plano-resumo-lista--colunas {
    grid-template-columns: 1fr;
  }
}

.plano-resumo-cta {
  margin-top: 26px;
  width: 100%;
}

@media (max-width: 640px) {
  .plano-resumo-card {
    max-width: 100%;
    padding: 24px 18px;
    border-radius: 18px;
  }
}

.wizard-plans-loading,
.wizard-plans-error {
  margin-top: 2.5rem;
  width: 100%;
  max-width: 56rem;
}

.wizard-plans-empty {
  margin-top: 2.5rem;
  width: 100%;
  max-width: 36rem;
  text-align: center;
}

.plans-grid-ref {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  width: 100%;
  max-width: 56rem;
  margin: 2.5rem auto 0;
  box-sizing: border-box;
}

@media (min-width: 640px) {
  .plans-grid-ref {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .plans-grid-ref {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

/* Centraliza os cards quando há 1 ou 2 planos */
.plans-grid-ref--few {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
}

.plans-grid-ref--few .plan-card-ref {
  flex: 0 1 min(320px, 100%);
  max-width: 360px;
}

.plan-card-ref {
  position: relative;
  display: block;
  width: 100%;
  text-align: left;
  padding: 1.5rem;
  border-radius: 1.5rem;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: #ffffff;
  box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: border-color 0.3s ease, box-shadow 0.3s ease, transform 0.2s ease;
  box-sizing: border-box;
}

@media (min-width: 768px) {
  .plan-card-ref {
    padding: 2rem;
  }
}

.plan-card-ref:hover {
  border-color: rgba(52, 137, 77, 0.55);
}

.plan-card-ref--highlighted {
  border-width: 2px;
  border-color: rgba(52, 137, 77, 0.35);
}

.plan-card-ref--recommended {
  border-width: 2px;
  border-color: rgba(37, 99, 235, 0.4);
}

.plan-card-ref--recommended:hover {
  border-color: rgba(37, 99, 235, 0.55);
}

.plan-card-ref--highlighted.plan-card-ref--recommended {
  border-color: rgba(52, 137, 77, 0.35);
  box-shadow: 0 0 0 1px rgba(37, 99, 235, 0.3);
}

.plan-card-ref__badges {
  position: absolute;
  top: -0.75rem;
  right: 1rem;
  left: 1rem;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
}

.plan-badge-ref {
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.25;
}

.plan-badge-ref--recomendado {
  background: #dbeafe;
  color: #1e40af;
  border: 1px solid rgba(37, 99, 235, 0.45);
}

.plan-badge-ref--completo {
  background: #d1fae5;
  color: #064e3b;
  border: 1px solid rgba(5, 150, 105, 0.45);
}

.plan-card-ref__icon-wrap {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  background: rgba(241, 245, 249, 0.95);
  color: #166534;
  transition: background 0.3s ease;
}

.plan-card-ref:hover .plan-card-ref__icon-wrap {
  background: rgba(52, 137, 77, 0.1);
}

.plan-card-ref__icon-wrap--highlight {
  background: rgba(52, 137, 77, 0.12);
}

.plan-card-ref:hover .plan-card-ref__icon-wrap--highlight {
  background: rgba(52, 137, 77, 0.18);
}

.plan-card-ref__icon-wrap--recommended {
  background: rgba(37, 99, 235, 0.1);
  color: #1d4ed8;
}

.plan-card-ref:hover .plan-card-ref__icon-wrap--recommended {
  background: rgba(37, 99, 235, 0.16);
}

.plan-card-ref__icon {
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
}

.plan-card-ref__title {
  margin: 0 0 0.375rem;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.125rem;
  font-weight: 500;
  line-height: 1.3;
  color: #0f172a;
}

.plan-card-ref__desc {
  margin: 0 0 1rem;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #64748b;
  min-height: 2.5rem;
}

.plan-card-ref__price {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.125rem 0.25rem;
}

.plan-card-ref__price-prefix,
.plan-card-ref__price-suffix {
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #64748b;
}

.plan-card-ref__price-value {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.25rem;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: #0f172a;
  margin-left: 0.125rem;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #6b7280;
}

.spinner {
  width: 46px;
  height: 46px;
  border: 4px solid #e5e7eb;
  border-top-color: #1f6f36;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-box {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  padding: 14px 16px;
  color: #b91c1c;
  margin-top: 16px;
  max-width: 560px;
}

.btn-primary {
  margin-top: 10px;
  background: #1f6f36;
  color: #ffffff;
  border: none;
  border-radius: 999px;
  padding: 10px 14px;
  font-weight: 800;
  cursor: pointer;
}

.btn-primary:hover {
  background: #195f2f;
}

.notice {
  margin: 18px auto 0;
  max-width: 560px;
  background: rgba(239, 246, 255, 0.8);
  border: 1px solid rgba(191, 219, 254, 1);
  color: #1e40af;
  border-radius: 18px;
  padding: 12px 14px;
  text-align: center;
  line-height: 1.6;
}

@media (max-width: 980px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }

  .hero-highlights {
    grid-template-columns: 1fr;
  }
}
</style>

